import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useForm from '@afosto/utils-react/validation/useForm';
import useIntl from '@afosto/utils-react/intl/useIntl';
import useRegisterInput from '../Form/useRegisterInput';
import FileUpload from '../FileUpload';
import Form from '../Form';
import Checkbox from '../Checkbox';
import Select from '../Select';
import Spreadsheet from '../Spreadsheet';
import TextField from '../TextField';
import Box from '@material-ui/core/Box';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import LoadingWrapper from '@afosto/components-react/LoadingWrapper';
import { dataset as datasetApi } from '@afosto/content/api/Dataset';
import convertToLetter from '@afosto/utils/numberToLetter';
import scheduleTask from '@afosto/utils-react/tasks/scheduleTask';
import validationSchema from './validations';
import translations from './translations';

const CSV_TYPES = ['text/csv', 'text/x-csv', 'application/x-csv'];

const DatasetForm = ({
  dataset,
  onCancel,
  onSubmit,
  readOnly,
  renderActions,
  showExistingDataset,
}) => {
  const intl = useIntl();
  const [availableDatasets, setAvailableDatasets] = useState([]);

  const handleSubmit = async ({
    data,
    file,
    firstRowAsColumns,
    existingDataset,
    reference,
    type,
  }) => {
    try {
      let datasetResponse = null;

      if (type === 'existingDataset') {
        datasetResponse = await datasetApi.getDataset(existingDataset);
      } else {
        datasetResponse = await datasetApi.createDataset({ reference });
      }

      const currentDataset = datasetResponse?.data;
      let attachResponse = null;

      if (type === 'file') {
        attachResponse = await datasetApi.attachFile(currentDataset?.id, {
          file_id: file,
        });
      }

      if (type === 'spreadsheet') {
        const [firstRow, ...otherRows] = data;
        const columns = firstRowAsColumns ? firstRow ?? {} : {};
        const columnValues = Object.values(columns);
        const rows = firstRowAsColumns ? otherRows ?? [] : data;
        const formattedData = rows.reduce((acc, row, rowIdx) => {
          const hasColumns = Object.keys(columns).length > 0;
          let formattedRow = {};

          if (hasColumns) {
            formattedRow = columnValues.reduce((cellsAcc, column, cellIdx) => {
              const values = Object.values(row);
              return { ...cellsAcc, [column]: values[cellIdx] ?? null };
            }, {});
          } else {
            formattedRow = Object.values(row).reduce((cellsAcc, value, cellIdx) => {
              const columnIsDefined = columnValues[cellIdx] && columnValues[cellIdx] !== '';
              const column = columnIsDefined ? columnValues[cellIdx] : convertToLetter(cellIdx + 1);
              return { ...cellsAcc, [column]: value };
            }, {});
          }

          return [...acc, { reference: `${rowIdx}`, data: formattedRow }];
        }, []);

        attachResponse = await datasetApi.attachData(currentDataset?.id, formattedData);
      }

      if (attachResponse) {
        const taskId = attachResponse.headers?.['x-task-id'];
        await scheduleTask(taskId);
      }

      if (onSubmit && typeof onSubmit === 'function') {
        await onSubmit(datasetResponse);
      }

      return datasetResponse;
    } catch (err) {
      return Promise.reject(err);
    }
  };

  const form = useForm({
    validationSchema,
    defaultValues: {
      data: [],
      existingDataset: dataset?.id ?? '',
      file: null,
      firstRowAsColumns: false,
      reference: '',
      type: dataset ? 'existingDataset' : '',
    },
    onSubmit: handleSubmit,
    onCancel,
  });

  useRegisterInput('data', true, form);
  useRegisterInput('file', true, form);

  const { setValue, watch } = form;
  const typeValue = watch('type');

  useEffect(() => {
    const fetchDatasets = async () => {
      try {
        const response = await datasetApi.listDatasets();
        const { data: datasets = [] } = response;

        setAvailableDatasets(
          datasets.map(({ reference, id }) => ({ label: reference, value: id })),
        );
      } catch (err) {
        setAvailableDatasets([]);
      }
    };

    if (typeValue !== 'existingDataset') {
      setValue('existingDataset', '');
    }

    if (typeValue !== 'file') {
      setValue('file', null);
    }

    if (typeValue !== 'spreadsheet') {
      setValue('data', []);
    }

    if (typeValue === 'existingDataset') {
      fetchDatasets().catch(() => {
        // Catch only
      });
    }
  }, [typeValue, setValue]);

  return (
    <LoadingWrapper
      isLoading={form.isSubmitting}
      message={intl.formatMessage(translations.loadingMessage)}
    >
      <Form form={form} renderActions={renderActions}>
        <Select
          name="type"
          label={intl.formatMessage(translations.fields.type.label)}
          suggestions={[
            {
              value: 'file',
              label: intl.formatMessage(translations.types.file.label),
            },
            {
              value: 'spreadsheet',
              label: intl.formatMessage(translations.types.spreadsheet.label),
            },
            ...(showExistingDataset
              ? [
                  {
                    value: 'existingDataset',
                    label: intl.formatMessage(translations.types.existingDataset.label),
                  },
                ]
              : []),
          ]}
          inline={readOnly}
          readOnly={readOnly}
        />

        {(typeValue === 'file' || typeValue === 'spreadsheet') && (
          <TextField
            name="reference"
            label={intl.formatMessage(translations.fields.reference.label)}
            inline={readOnly}
            readOnly={readOnly}
          />
        )}

        {typeValue === 'file' && (
          <Box pt={2}>
            <FormControl variant="outlined" error={form?.errors?.file} fullWidth>
              <FileUpload
                onUploadFile={file => setValue('file', file.id)}
                onRemoveFile={() => setValue('file', null)}
                onRevertFile={() => setValue('file', null)}
                allowFileSizeValidation
                allowFileTypeValidation
                acceptedFileTypes={CSV_TYPES}
                maxTotalFileSize="8MB"
                uploadPath="/datasets"
                fileValidateTypeDetectType={(source, type) =>
                  new Promise((resolve, reject) => {
                    if (type && type !== '' && CSV_TYPES.includes(type)) {
                      resolve('text/csv');
                    } else if (source?.name?.indexOf('.csv') > -1) {
                      resolve('text/csv');
                    } else {
                      reject();
                    }
                  })
                }
              />
              <FormHelperText>{form?.errors?.file?.message}</FormHelperText>
            </FormControl>
          </Box>
        )}

        {typeValue === 'spreadsheet' && (
          <Box pt={1}>
            <Checkbox
              name="firstRowAsColumns"
              label={intl.formatMessage(translations.fields.firstRowAsColumns.label)}
            />
            <Box pt={2}>
              <FormControl variant="outlined" error={form?.errors?.data} fullWidth>
                <Spreadsheet onChange={value => setValue('data', value)} />
                <FormHelperText>{form?.errors?.data?.message}</FormHelperText>
              </FormControl>
            </Box>
          </Box>
        )}

        {typeValue === 'existingDataset' && (
          <Select
            name="existingDataset"
            label={intl.formatMessage(translations.fields.dataset.label)}
            suggestions={availableDatasets}
            inline={readOnly}
            readOnly={readOnly}
          />
        )}
      </Form>
    </LoadingWrapper>
  );
};

DatasetForm.propTypes = {
  dataset: PropTypes.object,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  readOnly: PropTypes.bool,
  renderActions: PropTypes.bool,
  showExistingDataset: PropTypes.bool,
};

DatasetForm.defaultProps = {
  dataset: undefined,
  onCancel: undefined,
  onSubmit: undefined,
  readOnly: false,
  renderActions: true,
  showExistingDataset: false,
};

export default DatasetForm;
